import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  createVideoUploadTargets,
  fileUploadMeta,
  getVideoUploadJob,
  listCategories,
  putFileToPresignedUrl,
  startVideoRegistration,
  toAdminApiErrorMessage,
  toJobErrorMessage,
} from '@/api/adminVideos';
import { getArchiveVideoShareUrl } from '@/api/content';
import { readVideoDurationSeconds, validateAdminVideoForm } from '@/config/adminVideoUpload';

const EMPTY_FORM = {
  videoFile: null,
  thumbnailFile: null,
  materialFile: null,
  title: '',
  categoryId: '',
  questionnaireUrl: '',
  durationSeconds: null,
};

const POLL_INTERVAL_MS = 5000;

/**
 * 「入力 → 確認 → アップロード → 受付完了」の状態遷移をまとめる。
 *
 * アップロードは API Gateway / Lambda を通さず署名付きURLでS3へ直接送る（Lambdaのペイロード上限と
 * 実行時間では動画を通せない）。変換は MediaConvert のジョブとして走る。
 *
 * 変換完了後の videos への登録は EventBridge 経由でサーバー側が行うため、**ジョブ投入後は
 * ブラウザを閉じてよい**。開いたままの場合だけポーリングして進捗を表示する。
 * 共有用URLは識別子（slug）で組み立てられるので、受付時点で確定して表示できる。
 * オリジンは表示中のサイトではなく公開アーカイブサイトに固定する（テスト環境から登録しても
 * そのまま受講者へ渡せるURLにするため）。
 */
export function useAdminVideoCreate({ enabled = true } = {}) {
  const [step, setStep] = useState('form');
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [uploadProgress, setUploadProgress] = useState({ video: 0, thumbnail: 0, material: 0 });
  const [jobId, setJobId] = useState(null);
  const [publicId, setPublicId] = useState(null);
  const [transcodeProgress, setTranscodeProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);
  const [result, setResult] = useState(null);

  const categoriesQuery = useQuery({
    queryKey: ['admin-categories'],
    queryFn: listCategories,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!form.thumbnailFile) {
      setThumbnailPreviewUrl(null);
      return undefined;
    }
    const objectUrl = URL.createObjectURL(form.thumbnailFile);
    setThumbnailPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [form.thumbnailFile]);

  // S3へ送っている間の離脱だけ止める。変換中はサーバー側で完結するので閉じてよい
  useEffect(() => {
    if (phase !== 'uploading') return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [phase]);

  const setField = useCallback((name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => {
      if (!(name in current)) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  }, []);

  const setFile = useCallback(
    (name, file) => {
      setField(name, file ?? null);
      if (name !== 'videoFile') return;

      setField('durationSeconds', null);
      if (!file) return;
      readVideoDurationSeconds(file).then((seconds) => {
        setForm((current) => (current.videoFile === file ? { ...current, durationSeconds: seconds } : current));
      });
    },
    [setField]
  );

  const goToConfirm = useCallback(() => {
    const validation = validateAdminVideoForm(form);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return false;
    setStep('confirm');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    return true;
  }, [form]);

  const backToForm = useCallback(() => {
    setStep('form');
    setPhase('idle');
    setErrorMessage(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const startUpload = useCallback(async () => {
    const validation = validateAdminVideoForm(form);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      setStep('form');
      return;
    }

    setStep('uploading');
    setPhase('uploading');
    setErrorMessage(null);
    setJobId(null);
    setPublicId(null);
    setTranscodeProgress(0);
    setUploadProgress({ video: 0, thumbnail: 0, material: form.materialFile ? 0 : 1 });

    const uploadOne = async (key, target, file) => {
      if (!target?.uploadUrl) throw new Error(`upload_target_missing:${key}`);
      await putFileToPresignedUrl({
        url: target.uploadUrl,
        file,
        contentType: target.contentType,
        onProgress: (ratio) => setUploadProgress((current) => ({ ...current, [key]: ratio })),
      });
      setUploadProgress((current) => ({ ...current, [key]: 1 }));
    };

    try {
      const issued = await createVideoUploadTargets({
        video: fileUploadMeta(form.videoFile),
        thumbnail: fileUploadMeta(form.thumbnailFile),
        material: fileUploadMeta(form.materialFile),
      });
      const targets = issued?.targets ?? {};

      await uploadOne('video', targets.video, form.videoFile);
      await uploadOne('thumbnail', targets.thumbnail, form.thumbnailFile);
      if (form.materialFile && targets.material) {
        await uploadOne('material', targets.material, form.materialFile);
      }

      const registration = await startVideoRegistration({
        uploadId: issued.uploadId,
        categoryId: Number(form.categoryId),
        title: String(form.title).trim(),
        questionnaireUrl: String(form.questionnaireUrl).trim(),
        durationSeconds: form.durationSeconds ?? null,
        sourceObjectKey: targets.video.objectKey,
        thumbnailObjectKey: targets.thumbnail.objectKey,
        materialObjectKey: form.materialFile && targets.material ? targets.material.objectKey : null,
      });

      const acceptedPublicId = registration?.publicId ?? null;
      setPublicId(acceptedPublicId);
      setJobId(registration?.jobId ?? null);
      // 共有用URLは動画識別子で引けるので、この時点で確定できる（有効になるのは変換完了後）
      setResult({
        videoId: null,
        publicId: acceptedPublicId,
        title: String(form.title).trim(),
        shareUrl: getArchiveVideoShareUrl(acceptedPublicId),
      });
      setPhase('transcoding');
      setStep('accepted');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      setPhase('failed');
      setErrorMessage(toAdminApiErrorMessage(error, 'アップロードに失敗しました。'));
    }
  }, [form]);

  const jobQuery = useQuery({
    queryKey: ['admin-video-upload-job', jobId],
    queryFn: () => getVideoUploadJob(jobId),
    enabled: Boolean(enabled && jobId && phase === 'transcoding'),
    refetchInterval: POLL_INTERVAL_MS,
    // 既定では非アクティブなタブで止まってしまう。別タブで作業していても表示が追従するようにする
    // （完了・失敗になれば enabled が false になり自動的に止まる）
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    retry: 2,
  });

  useEffect(() => {
    const data = jobQuery.data;
    if (!data || phase !== 'transcoding') return;

    if (typeof data.progress === 'number') setTranscodeProgress(data.progress);

    if (data.status === 'completed') {
      setResult((current) => {
        const completedPublicId = data.publicId ?? current?.publicId ?? null;
        return {
          ...current,
          videoId: data.videoId ?? null,
          publicId: completedPublicId,
          title: data.title ?? current?.title ?? '',
          // サーバーの shareUrl はLambdaの CORS_ALLOW_ORIGIN 由来でテスト環境のドメインが入りうる。
          // 受付時点と同じ公開アーカイブサイトのURLで通す
          shareUrl: getArchiveVideoShareUrl(completedPublicId) ?? current?.shareUrl ?? null,
        };
      });
      setPhase('completed');
      return;
    }

    if (data.status === 'failed' || data.status === 'canceled') {
      setPhase('failed');
      setErrorMessage(toJobErrorMessage(data.error));
    }
  }, [jobQuery.data, phase]);

  // ポーリングが一時的に失敗しても登録自体はサーバー側で進むため、状態は変えず表示だけ添える
  const pollErrorMessage =
    phase === 'transcoding' && jobQuery.isError
      ? toAdminApiErrorMessage(jobQuery.error, '変換状況の取得に失敗しました。')
      : null;

  const reset = useCallback(() => {
    setForm(EMPTY_FORM);
    setErrors({});
    setStep('form');
    setPhase('idle');
    setUploadProgress({ video: 0, thumbnail: 0, material: 0 });
    setJobId(null);
    setPublicId(null);
    setTranscodeProgress(0);
    setErrorMessage(null);
    setResult(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return {
    step,
    form,
    errors,
    thumbnailPreviewUrl,
    phase,
    uploadProgress,
    transcodeProgress,
    errorMessage,
    pollErrorMessage,
    result,
    publicId,
    categories: categoriesQuery.data ?? [],
    categoriesLoading: categoriesQuery.isLoading,
    categoriesError: categoriesQuery.isError
      ? toAdminApiErrorMessage(categoriesQuery.error, 'カテゴリ一覧の取得に失敗しました。')
      : null,
    refetchCategories: categoriesQuery.refetch,
    setField,
    setFile,
    goToConfirm,
    backToForm,
    startUpload,
    reset,
  };
}
